import {
    Box,
    Button,
    Skeleton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import {
    Bike,
    Car,
    Check,
    ChevronDown,
    Eye,
    Factory,
    Plus,
    Scooter,
    User,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import * as Yup from 'yup';
import { httpClient } from '../../common/httpclient';
import Dropdown from '../../component/dropdown/Dropdown';
import TextAreaWithCounter from '../../component/input/TextAreaWithCounter';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import MobileVehicleForm from '../../component/vehicle/MobileVehicleForm';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import NewCustomer from '../../component/NewCustomer';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import ManufacturersModal from './ManufacturersModal';

/**
 * Seção colapsável do formulário — copiada 1:1 de `suppliers/Form.js`/
 * `customers/Form.js`/`products/Form.js` (DESIGN-SYSTEM.md §5.5 "Accordion
 * insertion form" / §6.2).
 */
function AccordionSection({ open, complete, icon: Icon, title, subtitle, onToggle, children }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: '1.5px solid',
                borderColor: open ? 'primary.main' : 'divider',
                borderRadius: `${theme.customRadius.hero}px`,
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                boxShadow: open ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                transition: 'border-color .2s, box-shadow .2s',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 20px', cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                    }}
                >
                    <Icon size={19} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
                </Box>
                {complete && (
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Check size={12} strokeWidth={3} />
                    </Box>
                )}
                <ChevronDown
                    size={16}
                    color={theme.palette.text.disabled}
                    style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                />
            </Box>
            <Box sx={{ maxHeight: open ? 2000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
                <Box sx={{ p: '16px 20px 24px' }}>{children}</Box>
            </Box>
        </Box>
    );
}

function PreviewRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: 'text.primary', textAlign: 'right', wordBreak: 'break-word' }}>
                {value || '—'}
            </Box>
        </Box>
    );
}

// Radio-card de tipo de veículo (Carro/Moto/Bicicleta) — mesma casca visual
// do `TypeCard` de `suppliers/Form.js` (Pessoa Jurídica/Pessoa Física),
// DESIGN-SYSTEM.md §5.5/§5.13, mas sem convenção de cor por tipo (o domínio
// "tipo de veículo" não está mapeado em §4 — as 3 opções usam sempre o
// destaque `primary`).
function VehicleTypeCard({ active, icon: Icon, title, description, onClick }) {
    const theme = useTheme();
    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                border: '1.5px solid',
                borderColor: active ? 'primary.main' : 'divider',
                borderRadius: '14px',
                p: '14px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                backgroundColor: active ? 'info.bg' : 'background.subtle',
                transition: 'border-color .15s, background-color .15s',
                '&:hover': { borderColor: active ? 'primary.main' : 'text.disabled' },
            }}
        >
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    backgroundColor: active ? 'primary.main' : 'background.paper',
                    color: active ? '#fff' : 'text.disabled',
                    border: '1px solid',
                    borderColor: active ? 'primary.main' : 'divider',
                    transition: 'background-color .15s, color .15s',
                }}
            >
                <Icon size={16} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: '2px' }}>{title}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.35 }}>{description}</Typography>
            </Box>
            {active && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Check size={10} strokeWidth={3} />
                </Box>
            )}
        </Box>
    );
}

// Link discreto abaixo de um campo (`.new-inline` do protótipo) — mesma
// cópia local usada em `pages/service_order/selectorClient.js` ("Novo
// cliente"/"Novo veículo"), reaproveitada aqui para "Novo cliente" e
// "Gerenciar fabricantes".
function NewInlineLink({ icon: Icon = Plus, label, onClick, disabled }) {
    return (
        <Box
            onClick={disabled ? undefined : onClick}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                mt: '6px',
                fontSize: '11.5px',
                fontWeight: 700,
                color: disabled ? 'text.disabled' : 'primary.main',
                cursor: disabled ? 'default' : 'pointer',
                '&:hover': disabled ? {} : { opacity: 0.75 },
            }}
        >
            <Icon size={12} strokeWidth={2.5} />
            {label}
        </Box>
    );
}

const VEHICLE_TYPES = [
    { id: 'car', label: 'Carro', description: 'Veículo de passeio ou utilitário.', icon: Car },
    { id: 'motorcycle', label: 'Moto', description: 'Motocicletas e scooters.', icon: Scooter },
    { id: 'bicycle', label: 'Bicicleta', description: 'Identificada por SN (Serial Number).', icon: Bike },
];

const requiredFieldLabel = (text) => (
    <>
        {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
    </>
);

export default function Form() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell && id ? 'Editar veículo' : '');
    // Largura real do menu lateral — usada para alinhar o rodapé de ações
    // fixo (`.action-footer` do protótipo) com a borda do conteúdo. Ref:
    // `suppliers/Form.js`/`customers/Form.js`/`products/Form.js`.
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [loadingManufacturers, setLoadingManufacturers] = useState(false);
    // Seções do acordeão abertas de forma independente (não exclusiva): cada
    // seção guarda seu próprio estado de expansão, permitindo múltiplas
    // seções abertas ao mesmo tempo.
    const [openSections, setOpenSections] = useState(['main']);
    const isSectionOpen = (key) => openSections.includes(key);
    const toggleSection = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
    const ensureSectionOpen = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev : [...prev, key]
    ));
    const [openDialogNewCustomer, setOpenDialogNewCustomer] = useState(false);
    const [openManufacturersModal, setOpenManufacturersModal] = useState(false);

    const goBack = () => navigate('/vehicles');

    const validationSchema = Yup.object({
        customer_id: Yup.string().required('Cliente é obrigatório'),
        type: Yup.string().required('Tipo é obrigatório'),
        manufacturer_id: Yup.string().required('Fabricante é obrigatório'),
        model: Yup.string().required('Modelo é obrigatório'),
        fab_year: Yup.number()
            .required('Ano de fabricação é obrigatório')
            .min(1900, 'Ano de fabricação inválido')
            .max(new Date().getFullYear() + 1, 'Ano de fabricação inválido'),
        mod_year: Yup.number()
            .required('Ano do modelo é obrigatório')
            .min(1900, 'Ano do modelo inválido')
            .max(new Date().getFullYear() + 1, 'Ano do modelo inválido'),
        license_plate: Yup.string()
            .when('type', {
                is: (type) => type !== 'bicycle',
                then: (schema) => schema.required('Placa é obrigatória'),
                otherwise: (schema) => schema.notRequired()
            }),
        chassi: Yup.string()
            .when('type', {
                is: (type) => type !== 'bicycle',
                then: (schema) => schema.required('Chassi é obrigatório').length(17, 'Chassi deve ter 17 caracteres'),
                otherwise: (schema) => schema.required('SN (Serial Number) é obrigatório')
            }),
        color: Yup.string().required('Cor é obrigatória'),
        kilometer: Yup.number()
            .min(0, 'Quilometragem deve ser maior ou igual a 0'),
        observation: Yup.string().max(500, 'Observação deve ter no máximo 500 caracteres')
    });

    const handleShowSuccessSnackbar = (text) => {
        setOpenSnackbar(true);
        setMessage(text);
        setSeverity('success');
    };

    const handleShowErrorSnackbar = (responseData) => {
        setOpenSnackbar(true);
        let text = 'Erro desconhecido';
        if (typeof responseData === 'string') {
            text = responseData;
        } else if (responseData && typeof responseData === 'object') {
            text = responseData.message || responseData.id || JSON.stringify(responseData);
        }
        setMessage(text);
        setSeverity('error');
    };

    const formik = useFormik({
        initialValues: {
            customer_id: '',
            type: '',
            manufacturer_id: '',
            model: '',
            fab_year: '',
            mod_year: '',
            license_plate: '',
            chassi: '',
            color: '',
            kilometer: '',
            observation: ''
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                if (id) {
                    const response = await httpClient.put(`/vehicles/${id}`, values);
                    if (response.status === 204 || response.status === 200) {
                        handleShowSuccessSnackbar('Veículo atualizado com sucesso!');
                        setTimeout(() => navigate('/vehicles'), 1200);
                    } else {
                        handleShowErrorSnackbar(response.data);
                    }
                } else {
                    const response = await httpClient.post('/vehicles', values);
                    if (response.status === 201 || response.status === 200) {
                        handleShowSuccessSnackbar('Veículo cadastrado com sucesso!');
                        setTimeout(() => navigate('/vehicles'), 1200);
                    } else {
                        handleShowErrorSnackbar(response.data);
                    }
                }
            } catch (error) {
                console.error('Error saving vehicle:', error);
                handleShowErrorSnackbar(error.response?.data || error.message);
            } finally {
                setLoading(false);
            }
        },
    });

    const fetchCustomers = async () => {
        try {
            const response = await httpClient.get('/customers');
            const list = (response.data || []).map((customer) => ({
                id: customer.ID,
                name: customer.Name,
                sub: customer.Cellphone || '',
            }));
            setCustomers(list);
            return list;
        } catch (error) {
            console.error('Error fetching customers:', error);
            return [];
        }
    };

    const fetchManufacturers = async (type) => {
        if (!type) {
            setManufacturers([]);
            return;
        }
        try {
            setLoadingManufacturers(true);
            const response = await httpClient.get(`/vehicles/manufacturers/${type}`);
            const list = Array.isArray(response.data)
                ? response.data.filter((item) => item && typeof item === 'object' && item.id && item.name)
                : [];
            setManufacturers(list);
        } catch (error) {
            console.error('Error fetching manufacturers:', error);
            setManufacturers([]);
        } finally {
            setLoadingManufacturers(false);
        }
    };

    const fetchVehicle = async () => {
        try {
            const response = await httpClient.get(`/vehicles/${id}`);
            const data = response.data || {};
            const vehicleData = {
                customer_id: data.CustomerID || data.customer_id || '',
                type: data.Type || data.type || '',
                manufacturer_id: data.ManufacturerID || data.manufacturer_id || '',
                model: data.Model || data.model || '',
                fab_year: data.FabYear || data.fab_year || '',
                mod_year: data.ModYear || data.mod_year || '',
                license_plate: data.LicensePlate || data.license_plate || '',
                chassi: data.Chassi || data.chassi || '',
                color: data.Color || data.color || '',
                kilometer: data.Kilometer || data.kilometer || '',
                // GET /vehicles/{id} (`VehicleViewResponse`, fv-api) não expõe
                // observação — o campo fica sempre vazio ao editar, mesmo
                // comportamento (bug preexistente, fora de escopo) do
                // formulário anterior.
                observation: data.Observation || data.observation || ''
            };
            if (vehicleData.fab_year) vehicleData.fab_year = parseInt(vehicleData.fab_year, 10);
            if (vehicleData.mod_year) vehicleData.mod_year = parseInt(vehicleData.mod_year, 10);
            if (vehicleData.kilometer) vehicleData.kilometer = parseInt(vehicleData.kilometer, 10);

            if (vehicleData.type) {
                await fetchManufacturers(vehicleData.type);
            }
            formik.setValues(vehicleData);
        } catch (error) {
            console.error('Error fetching vehicle:', error);
            handleShowErrorSnackbar('Erro ao carregar dados do veículo');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoadingData(true);
            try {
                await fetchCustomers();
                if (id) {
                    await fetchVehicle();
                }
            } finally {
                setLoadingData(false);
            }
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleChangeType = (value) => {
        if (value === formik.values.type) return;
        formik.setFieldValue('type', value);
        formik.setFieldValue('manufacturer_id', '');
        setManufacturers([]);
        if (value === 'bicycle') {
            formik.setFieldValue('license_plate', '');
        }
        fetchManufacturers(value);
    };

    const handleChangeManufacturer = (value) => formik.setFieldValue('manufacturer_id', value);
    const handleChangeCustomer = (value) => formik.setFieldValue('customer_id', value);

    const handleLicensePlateChange = (event) => {
        const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        const formattedValue = value.length > 3 ? `${value.slice(0, 3)}-${value.slice(3)}` : value;
        formik.setFieldValue('license_plate', formattedValue);
    };

    const handleOnSuccessCustomer = (customer) => {
        const rawId = customer?.id ?? customer?.ID ?? customer?.Id;
        const name = customer?.name ?? customer?.Name ?? customer?.tradeName ?? customer?.legalName ?? '';
        if (!rawId) {
            fetchCustomers();
            return;
        }
        const row = { id: String(rawId), name, sub: customer?.Cellphone || customer?.cell_phone || '' };
        setCustomers((prev) => {
            const sid = row.id;
            if (prev.some((c) => String(c.id) === sid)) {
                return prev.map((c) => (String(c.id) === sid ? { ...c, ...row } : c));
            }
            return [...prev, row];
        });
        formik.setFieldValue('customer_id', row.id);
        setOpenDialogNewCustomer(false);
    };

    const handleCloseManufacturersModal = () => {
        setOpenManufacturersModal(false);
        // Repopula o <select> Fabricante do tipo atual e recalcula
        // preview/anel — mesmo comportamento do protótipo
        // (`closeFabricantesModal()`).
        if (formik.values.type) {
            fetchManufacturers(formik.values.type);
        }
    };

    // Ref: `suppliers/Form.js`/`products/Form.js` `standaloneFieldSx`/
    // `standaloneInputBoxSx` — mesma casca visual de campo "ff-input" (52px,
    // radius 12px, fundo surface-2, label flutuante interno) aplicada aqui
    // verbatim, incl. tratamento de `<Select>` (Dropdown `labelVariant=
    // "static-micro"`, campo Fabricante) e `<textarea>` (Observação).
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
            '&.MuiInputBase-multiline': {
                height: 'auto',
                minHeight: '96px',
                alignItems: 'flex-start',
                padding: '24px 14px 7px 14px',
            },
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiSelect-select.MuiOutlinedInput-input': {
            padding: '0 14px',
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
        '& .MuiInputBase-multiline textarea': {
            resize: 'vertical',
        },
    };

    const standaloneFieldSx = {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };

    // Linha de opção do combo de cliente — mesmo padrão de
    // `service_order/selectorClient.js` (`.combo-opt`/`.oname`/`.osub`).
    const renderClientOption = (props, option) => (
        <li {...props} key={option.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '10px 12px' }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: theme.palette.text.primary }}>{option.name}</span>
            {option.sub && <span style={{ fontSize: 11, color: theme.palette.text.disabled }}>{option.sub}</span>}
        </li>
    );

    const isBicycle = formik.values.type === 'bicycle';

    // Seções + campos obrigatórios por seção — o anel de progresso é
    // calculado a partir da fração de seções completas (mesmo algoritmo de
    // `suppliers/Form.js`/`customers/Form.js`/`products/Form.js`).
    const isFieldFilled = (key) => Boolean(String(formik.values[key] ?? '').trim());
    const mainRequiredFields = [
        'type', 'fab_year', 'mod_year', 'manufacturer_id', 'model', 'color', 'chassi',
        ...(isBicycle ? [] : ['license_plate']),
    ];
    const isMainComplete = mainRequiredFields.every(isFieldFilled);
    const isCustomerComplete = isFieldFilled('customer_id');
    const requiredSectionsDone = [isMainComplete, isCustomerComplete].filter(Boolean).length;
    const progressPct = Math.round((requiredSectionsDone / 2) * 100);
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    const sections = [
        { key: 'main', title: 'Dados do veículo', subtitle: 'Placa, fabricante, modelo, ano, cor, chassi e km', icon: Car, complete: isMainComplete },
        { key: 'customer', title: 'Cliente e observações', subtitle: 'Quem é o proprietário do veículo', icon: User, complete: isCustomerComplete },
    ];

    // Preview ao vivo — ícone de carro no lugar do avatar/iniciais, placa em
    // destaque como título, fabricante+modelo como subtítulo, chip de ano
    // (DESIGN-SYSTEM.md §5.5, adaptado ao domínio de veículo por este item).
    const selectedManufacturer = manufacturers.find((m) => String(m.id) === String(formik.values.manufacturer_id));
    const manufacturerName = selectedManufacturer?.name || '';
    const previewPlate = formik.values.license_plate || (isBicycle ? formik.values.chassi : '') || 'Placa do veículo';
    const previewSub = (manufacturerName || formik.values.model)
        ? `${manufacturerName}${manufacturerName && formik.values.model ? ' · ' : ''}${formik.values.model}`
        : 'Preencha os dados ao lado';
    const yearChipLabel = formik.values.fab_year && formik.values.mod_year
        ? `Ano ${formik.values.fab_year}/${formik.values.mod_year}`
        : formik.values.fab_year || formik.values.mod_year
            ? `Ano ${formik.values.fab_year || formik.values.mod_year}`
            : 'Ano —';
    const selectedCustomer = customers.find((c) => String(c.id) === String(formik.values.customer_id));
    const kmFormatted = formik.values.kilometer
        ? `${Number(formik.values.kilometer).toLocaleString('pt-BR')} km`
        : '';

    if (loadingData) {
        return (
            <PageScaffold
                titlePageTarget="Veículos"
                links={[{ title: 'Veículos', link: '/vehicles' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                {isMobileShell ? (
                    <Stack spacing={1.25}>
                        {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={64} sx={{ borderRadius: '16px' }} />)}
                    </Stack>
                ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                    <Stack spacing={1.75}>
                        <Skeleton variant="rounded" height={320} sx={{ borderRadius: '18px' }} />
                        <Skeleton variant="rounded" height={200} sx={{ borderRadius: '18px' }} />
                    </Stack>
                    <Skeleton variant="rounded" height={420} sx={{ borderRadius: '20px' }} />
                </Box>
                )}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            titlePageTarget="Veículos"
            links={[{ title: 'Veículos', link: '/vehicles' }]}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            {isMobileShell ? (
                <MobileVehicleForm
                    formik={formik}
                    customers={customers}
                    manufacturers={manufacturers}
                    loadingManufacturers={loadingManufacturers}
                    onChangeType={handleChangeType}
                    onChangePlate={handleLicensePlateChange}
                    onNewCustomer={() => setOpenDialogNewCustomer(true)}
                    onManageManufacturers={() => setOpenManufacturersModal(true)}
                    onCancel={goBack}
                    loading={loading}
                />
            ) : (
            <Box>
                {/* form-hero — título + descrição + anel de progresso. */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', mb: 2.25 }}>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                            {id ? 'Editar veículo' : 'Novo veículo'}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                            Preencha por seções — o cartão ao lado mostra o resultado em tempo real.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                        <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                            <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                <circle
                                    cx="27"
                                    cy="27"
                                    r={RING_R}
                                    fill="none"
                                    strokeWidth="5"
                                    strokeLinecap="round"
                                    stroke={theme.palette.primary.main}
                                    strokeDasharray={RING_CIRC}
                                    strokeDashoffset={ringOffset}
                                    style={{ transition: 'stroke-dashoffset .35s ease' }}
                                />
                            </svg>
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                                {progressPct}%
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cadastro</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>completo conforme você preenche</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* step-pills */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.25 }}>
                    {sections.map((sec, idx) => {
                        const isSecOpen = isSectionOpen(sec.key);
                        return (
                            <Box
                                key={sec.key}
                                onClick={() => ensureSectionOpen(sec.key)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                    px: '14px',
                                    py: '8px',
                                    borderRadius: '24px',
                                    border: '1.5px solid',
                                    borderColor: isSecOpen ? 'primary.main' : 'divider',
                                    backgroundColor: isSecOpen ? 'info.bg' : 'background.paper',
                                    color: isSecOpen ? 'primary.main' : 'text.secondary',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 17,
                                        height: 17,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: sec.complete ? 'success.main' : (isSecOpen ? 'primary.main' : 'text.disabled'),
                                        backgroundColor: sec.complete ? 'success.main' : 'transparent',
                                        color: sec.complete ? '#fff' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 9,
                                        flexShrink: 0,
                                    }}
                                >
                                    {sec.complete ? <Check size={10} strokeWidth={3} /> : idx + 1}
                                </Box>
                                {sec.title}
                            </Box>
                        );
                    })}
                </Box>

                {/* form-columns — acordeão + preview sticky */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                    <Stack spacing={1.75} sx={{ minWidth: 0 }} component="form" onSubmit={formik.handleSubmit}>
                        <AccordionSection
                            open={isSectionOpen('main')}
                            complete={sections[0].complete}
                            icon={sections[0].icon}
                            title={sections[0].title}
                            subtitle={sections[0].subtitle}
                            onToggle={() => toggleSection('main')}
                        >
                            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '10px' }}>
                                Tipo de veículo <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: '12px', mb: 2.5 }}>
                                {VEHICLE_TYPES.map((vt) => (
                                    <VehicleTypeCard
                                        key={vt.id}
                                        active={formik.values.type === vt.id}
                                        icon={vt.icon}
                                        title={vt.label}
                                        description={vt.description}
                                        onClick={() => handleChangeType(vt.id)}
                                    />
                                ))}
                            </Box>
                            {formik.touched.type && formik.errors.type && (
                                <Typography sx={{ fontSize: 11.5, color: 'error.main', mt: -1.5, mb: 2 }}>{formik.errors.type}</Typography>
                            )}

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: isBicycle ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)' }, gap: 2, '& > *': { minWidth: 0 } }}>
                                {!isBicycle && (
                                    <TextField
                                        label={requiredFieldLabel('Placa')}
                                        fullWidth
                                        name="license_plate"
                                        value={formik.values.license_plate}
                                        onChange={handleLicensePlateChange}
                                        error={formik.touched.license_plate && Boolean(formik.errors.license_plate)}
                                        helperText={formik.touched.license_plate && formik.errors.license_plate}
                                        variant="outlined"
                                        inputProps={{ maxLength: 8, style: { textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 } }}
                                        sx={standaloneFieldSx}
                                    />
                                )}
                                <TextField
                                    label={requiredFieldLabel('Ano de fabricação')}
                                    type="number"
                                    fullWidth
                                    name="fab_year"
                                    value={formik.values.fab_year}
                                    onChange={formik.handleChange}
                                    error={formik.touched.fab_year && Boolean(formik.errors.fab_year)}
                                    helperText={formik.touched.fab_year && formik.errors.fab_year}
                                    variant="outlined"
                                    inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    label={requiredFieldLabel('Ano do modelo')}
                                    type="number"
                                    fullWidth
                                    name="mod_year"
                                    value={formik.values.mod_year}
                                    onChange={formik.handleChange}
                                    error={formik.touched.mod_year && Boolean(formik.errors.mod_year)}
                                    helperText={formik.touched.mod_year && formik.errors.mod_year}
                                    variant="outlined"
                                    inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                                    sx={standaloneFieldSx}
                                />
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mt: 2, '& > *': { minWidth: 0 } }}>
                                <Box>
                                    <Dropdown
                                        label={requiredFieldLabel('Fabricante')}
                                        data={manufacturers}
                                        selectedItem={formik.values.manufacturer_id}
                                        handleChange={handleChangeManufacturer}
                                        disabled={!formik.values.type}
                                        loading={loadingManufacturers}
                                        error={formik.touched.manufacturer_id && Boolean(formik.errors.manufacturer_id)}
                                        helperText={
                                            (formik.touched.manufacturer_id && formik.errors.manufacturer_id)
                                                || (loadingManufacturers ? 'Carregando fabricantes...' : (!formik.values.type ? 'Selecione o tipo de veículo primeiro' : ''))
                                        }
                                        variant="outlined"
                                        size="medium"
                                        // Select com label flutuante: reserva o topo do valor como
                                        // nos inputs vizinhos (Modelo), mantendo 32px p/ o ícone.
                                        sx={{
                                            ...standaloneFieldSx,
                                            '& .MuiSelect-select.MuiOutlinedInput-input': {
                                                padding: '21px 32px 7px 14px',
                                                display: 'block',
                                            },
                                        }}
                                    />
                                    <NewInlineLink icon={Factory} label="Gerenciar fabricantes" onClick={() => setOpenManufacturersModal(true)} />
                                </Box>
                                <TextField
                                    label={requiredFieldLabel('Modelo')}
                                    fullWidth
                                    name="model"
                                    value={formik.values.model}
                                    onChange={formik.handleChange}
                                    disabled={!formik.values.manufacturer_id}
                                    error={formik.touched.model && Boolean(formik.errors.model)}
                                    helperText={formik.touched.model && formik.errors.model}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mt: 2, '& > *': { minWidth: 0 } }}>
                                <TextField
                                    label={requiredFieldLabel('Cor')}
                                    fullWidth
                                    name="color"
                                    value={formik.values.color}
                                    onChange={formik.handleChange}
                                    error={formik.touched.color && Boolean(formik.errors.color)}
                                    helperText={formik.touched.color && formik.errors.color}
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    label="Km atual"
                                    type="number"
                                    fullWidth
                                    name="kilometer"
                                    value={formik.values.kilometer}
                                    onChange={formik.handleChange}
                                    error={formik.touched.kilometer && Boolean(formik.errors.kilometer)}
                                    helperText={formik.touched.kilometer && formik.errors.kilometer}
                                    variant="outlined"
                                    inputProps={{ min: 0 }}
                                    sx={standaloneFieldSx}
                                />
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <TextField
                                    label={requiredFieldLabel(isBicycle ? 'SN (Serial Number)' : 'Número do chassi')}
                                    fullWidth
                                    name="chassi"
                                    value={formik.values.chassi}
                                    onChange={formik.handleChange}
                                    error={formik.touched.chassi && Boolean(formik.errors.chassi)}
                                    helperText={formik.touched.chassi && formik.errors.chassi}
                                    variant="outlined"
                                    inputProps={{
                                        maxLength: isBicycle ? undefined : 17,
                                        style: { fontFamily: 'monospace', letterSpacing: 1, textTransform: 'uppercase' },
                                    }}
                                    sx={standaloneFieldSx}
                                />
                            </Box>
                        </AccordionSection>

                        <AccordionSection
                            open={isSectionOpen('customer')}
                            complete={sections[1].complete}
                            icon={sections[1].icon}
                            title={sections[1].title}
                            subtitle={sections[1].subtitle}
                            onToggle={() => toggleSection('customer')}
                        >
                            <Box>
                                <Dropdown
                                    mode="autocomplete"
                                    variant="outlined"
                                    size="medium"
                                    sx={standaloneFieldSx}
                                    label={requiredFieldLabel('Cliente')}
                                    data={customers}
                                    selectedItem={formik.values.customer_id}
                                    handleChange={handleChangeCustomer}
                                    error={formik.touched.customer_id && Boolean(formik.errors.customer_id)}
                                    helperText={formik.touched.customer_id && formik.errors.customer_id}
                                    renderOption={renderClientOption}
                                />
                                <NewInlineLink label="Novo cliente" onClick={() => setOpenDialogNewCustomer(true)} />
                            </Box>

                            <Box sx={{ mt: 2.5 }}>
                                <TextAreaWithCounter
                                    label="Observações (opcional)"
                                    maxLength={500}
                                    name="observation"
                                    value={formik.values.observation}
                                    handleChange={formik.handleChange}
                                    error={formik.touched.observation && Boolean(formik.errors.observation)}
                                    helperText={formik.touched.observation && formik.errors.observation}
                                    variant="outlined"
                                    rows={4}
                                    sx={standaloneFieldSx}
                                />
                            </Box>
                        </AccordionSection>
                    </Stack>

                    {/* preview sticky — placa em destaque, ícone de carro no lugar do
                        avatar/iniciais (adaptação do padrão contact-card ao domínio de
                        veículo, DESIGN-SYSTEM.md §5.5). */}
                    <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                        <Typography sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', mb: 1.25 }}>
                            <Eye size={13} /> Pré-visualização ao vivo
                        </Typography>

                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: `${theme.customRadius.hero}px`, backgroundColor: 'background.paper', boxShadow: theme.customShadows.md, overflow: 'hidden', mb: 1.75 }}>
                            <Box sx={{ height: 78, backgroundImage: theme.palette.gradient.brand, position: 'relative' }}>
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: '22px',
                                        backgroundColor: 'background.paper',
                                        border: '4px solid',
                                        borderColor: 'background.paper',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                        position: 'absolute',
                                        left: 20,
                                        bottom: -32,
                                        boxShadow: theme.customShadows.md,
                                    }}
                                >
                                    <Car size={30} strokeWidth={1.8} />
                                </Box>
                            </Box>
                            <Box sx={{ p: '42px 20px 20px' }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25, textTransform: 'uppercase' }}>
                                    {previewPlate}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.25 }}>
                                    {previewSub}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
                                    <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: '10px', py: '5px', borderRadius: '20px', backgroundColor: 'info.bg', color: 'primary.main' }}>
                                        {yearChipLabel}
                                    </Box>
                                </Box>
                                <Box sx={{ height: '1px', backgroundColor: 'divider', my: 2 }} />
                                <PreviewRow label="Fabricante" value={manufacturerName} />
                                <PreviewRow label="Modelo" value={formik.values.model} />
                                <PreviewRow label="Cor" value={formik.values.color} />
                                <PreviewRow label="Km atual" value={kmFormatted} />
                                <PreviewRow label="Cliente" value={selectedCustomer?.name} />
                            </Box>
                        </Box>

                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                            Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios. Você pode editar tudo depois de salvar.
                        </Typography>
                    </Box>
                </Box>

                {/* Espaçador para compensar o rodapé fixo abaixo. */}
                <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                {/* action-footer fixo — Cancelar/Salvar. */}
                <Box
                    sx={{
                        position: 'fixed',
                        left: actionFooterLeft,
                        right: 0,
                        bottom: 0,
                        zIndex: theme.zIndex.appBar,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                        px: 3,
                        py: 1.75,
                        pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                        pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                        pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                        transition: 'left .2s ease',
                    }}
                >
                    <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                        Campos com * são obrigatórios
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined" onClick={goBack}>Cancelar</Button>
                        <Button variant="contained" onClick={formik.handleSubmit} disabled={loading}>
                            {loading ? 'Salvando...' : (id ? 'Atualizar veículo' : 'Salvar veículo')}
                        </Button>
                    </Stack>
                </Box>
            </Box>
            )}

            <NewCustomer
                open={openDialogNewCustomer}
                setOpen={setOpenDialogNewCustomer}
                onSuccess={handleOnSuccessCustomer}
            />

            <ManufacturersModal
                open={openManufacturersModal}
                onClose={handleCloseManufacturersModal}
            />
        </PageScaffold>
    );
}
